import React, { useEffect, useState } from "react";
import { Button as MantineButton } from "@mantine/core";

import { Tool as EditorTool } from '../../../../artcloud.dash/source/scripts/components/common/editor/Utilities.jsx';
import { Frame as CampaignFrame } from '../../../../artcloud.dash/source/scripts/email/EmailRenderer.jsx';
import CampaignName from '../../../../artcloud.dash/source/scripts/email/utility/Name.jsx';
import Checkbox from "../common/input/Checkbox.jsx";
import { Component as Landmark } from '../common/container/Landmark.jsx';
import { Onboarding as Testimonial } from '../common/container/Testimonial.jsx';
import Device from '../common/repository/Device.jsx';
import { Frame as CollateralFrame } from '../../../../artcloud.collateral/source/RenderedCollateral.jsx';
import TestimonialRepository from "../common/repository/Testimonial.jsx";
import { Onboarding as Input } from '../common/input/Text.jsx';
import { AnnualSalesBracket, CareerStage, Inventory, Metric, User } from '../common/repository/Type.jsx';
import Request from "../common/Request.jsx";
import { CreateAccountScreen } from "../onboarding/CreateAccountScreen";
import { GetStartedScreen } from "../onboarding/GetStartedScreen";
import { IntentScreen } from "../onboarding/IntentScreen";
import { OnboardingMantineRoot } from "../onboarding/OnboardingMantineRoot";
import { OnboardingProfileScreen } from "../onboarding/OnboardingProfileScreen";
import { WelcomeScreen } from "../onboarding/WelcomeScreen";
import { galleryIntents, useIntentSelection } from "../onboarding/intentOptions";
import { emitAccountConverted, trackAccountConversion } from "../onboarding/v2/conversionTracking";
import { termsOfServiceUrl } from "../onboarding/v2/legalLinks";

// Intent.Type mirrors the server's SignupIntent enum, and it is over this object's own keys that
// the for...in loop in Step.WhatDoYouWant below iterates to build the 'OB: Intent Declared'
// beacon (calling Intent.displayType() on each set bit). Catalogue (256) and TrackSales (512) are
// not offered by any legacy screen today - ARTIST_INTENTS (onboarding/intentOptions.ts) has no bit
// for either, and the gallery arm offers the same fixed option set - so displayType()'s two
// matching cases below are unreachable from here for now. They stay anyway: the alternative is an
// incomplete mirror, where a bitmask that someday carries either bit would leave the funnel with
// `undefined` in place of the name.
//
// Valuations (1024), Provenance (2048) and Acquisitions (4096) - the three collector-only bits
// ACA-268 added - are unreachable from this file for a sharper reason than Catalogue/TrackSales:
// the legacy Collector flow never reaches Step.WhatDoYouWant at all (it jumps straight from the
// picker to Step.GalleryRegister, :410-413 below), and the v2 wizard reads its own goals'
// analyticsLabel straight off COLLECTOR_GOALS via analyticsLabelsFor, never through
// Intent.displayType(). They stay anyway, for the same reason as Catalogue/TrackSales: an
// incomplete mirror here is silent today and wrong the day either path changes.
//
// ManageStock (8192) and Certificates (16384) - the two jewelry-only bits ACA-269 added - are
// unreachable for the same reason as Catalogue/TrackSales, not the collector's reason: the legacy
// Jewelry flow does reach Step.WhatDoYouWant (_routeFromPersonaSelection, :432-435, falls through
// to the same setStep(Step.WhatDoYouWant) the gallery arm uses, unlike Collector's early return).
// What keeps these two bits out of `data` is the option list WhatDoYouWant renders its chips from
// - galleryIntents() (onboarding/intentOptions.ts), a fixed six-entry set with no bit for either -
// so toggling a chip here can never set them. They stay anyway, for the same reason as the rest of
// this mirror: an incomplete one is silent today and wrong the day either path changes.
const Intent = {
  Type: {
    Crm: 8,
    Website: 1,
    Collateral: 4,
    EmailCampaign: 2,
    BusinessOperations: 16,
    Marketplace: 32,
    MeetNewGalleries: 64,
    Consignments: 128,
    Catalogue: 256,
    TrackSales: 512,
    Valuations: 1024,
    Provenance: 2048,
    Acquisitions: 4096,
    ManageStock: 8192,
    Certificates: 16384
  },


  details: (value, user) => {
    switch (value) {
      case Intent.Type.Website:
        return { description: 'Custom, responsive, high-end design', display: `Create ${User.getTypeDisplay(user).toLowerCase()} website`, icon: '/assets/img/onboarding/create-gallery-website.svg' };
      case Intent.Type.EmailCampaign:
        return { description: 'Nurture and convert with great email', display: 'Send email campaigns', icon: '/assets/img/onboarding/send-email-campaigns.svg' };
      case Intent.Type.Collateral:
        return { description: 'Price lists, tear sheets, and more', display: 'Create collateral', icon: '/assets/img/onboarding/create-collateral.svg' };
      case Intent.Type.BusinessOperations:
        return { description: 'Everything you need to run your business', display: 'Business operations', icon: '/assets/img/onboarding/manage-inventory.svg' };
      case Intent.Type.Crm:
        return { description: 'Opportunity tracking & reminders', display: 'Sales tools', icon: '/assets/img/onboarding/sales-tools.svg' };
      case Intent.Type.Marketplace:
        return { description: 'List on ArtCloud to sell your art', display: 'Sell on the marketplace', icon: '/assets/img/onboarding/sell-on-marketplace.svg' };
    }
  },

  displayType: (type) => {
    switch (type) {
      case Intent.Type.Website:
        return 'Website';
      case Intent.Type.EmailCampaign:
        return 'Email Campaign';
      case Intent.Type.Collateral:
        return 'Collateral';
      case Intent.Type.Crm:
        return 'CRM';
      case Intent.Type.BusinessOperations:
        return 'Business Operations';
      case Intent.Type.Marketplace:
        return 'Marketplace';
      case Intent.Type.MeetNewGalleries:
        return 'Meet New Galleries';
      case Intent.Type.Consignments:
        return 'Consignments';
      case Intent.Type.Catalogue:
        return 'Catalogue';
      case Intent.Type.TrackSales:
        return 'Track Sales';
      case Intent.Type.Valuations:
        return 'Valuations';
      case Intent.Type.Provenance:
        return 'Provenance';
      case Intent.Type.Acquisitions:
        return 'Acquisitions';
      case Intent.Type.ManageStock:
        return 'Manage Stock';
      case Intent.Type.Certificates:
        return 'Certificates';
    }
  }
}

const Preview = {
  CollateralType: {
    Certificate: 'certificates',
    Pricelist: 'pricelists',
    Tearsheet: 'tearsheets',

    display: (collateralType) => {
      switch (collateralType) {
        case Preview.CollateralType.Certificate:
          return 'Certificate of Authenticity';
        case Preview.CollateralType.Pricelist:
          return 'Pricelist';
        case Preview.CollateralType.Tearsheet:
          return 'Tearsheet';
      }
    },

    valueOf: (collateralType) => {
      switch (collateralType) {
        case Preview.CollateralType.Certificate:
          return 5;
        case Preview.CollateralType.Pricelist:
          return 1;
        case Preview.CollateralType.Tearsheet:
          return 2;
      }
    }
  },

  Type: {
    Collateral: 'collateral',
    Email: 'email',
    Website: 'website'
  }
}

const Step = {
  WhoAreYou: 'type',
  WhatDoYouWant: 'intent',
  BrandPackagePreview: 'brand',
  ArtistRegister: 'register',
  Welcome: 'welcome',
  Profile: 'aboutyou',
  GetStarted: 'getstarted',
  GalleryRegister: 'profile'
}

const ProfileNextStep = Step.WhatDoYouWant;

// ACA-266 Fase 5: the v2 picker's PersonaId (personaOptions.ts) is not the User.* enum - TS can't
// import Type.jsx's named exports (§2.6 of the plan), so the mapping stays here, the one place
// that already owns the Jewelry coercion the router below reuses.
//
// The `artist` and `collector` entries are both dead in practice now: ACA-267 Phase 5 gave v2 its
// own Artist branch, and ACA-268 did the same for Collector, so v2's own picker no longer hands
// either off through initialType. Both stay in place on purpose: this map is defensive, not a
// record of every route that currently uses it, and initialType can still carry "artist" or
// "collector" through some other caller this file does not control.
const INITIAL_TYPE_TO_USER_TYPE = {
  artist: User.Artist,
  collector: User.Collector,
  jewelry: User.Jewelry,
};

const ONBOARDING_STATE_KEY = 'ac_onboarding';

const _loadOnboardingState = () => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(ONBOARDING_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const _saveOnboardingState = (patch) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const current = _loadOnboardingState() || {};
    sessionStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify({ ...current, ...patch }));
  } catch (_) {
    /* best-effort: never block the flow on storage */
  }
};

const _clearOnboardingState = () => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ONBOARDING_STATE_KEY);
    }
    Artcloud.cookie.remove(Artcloud.auth.keys.cookies.onboardingId);
    Artcloud.cookie.remove(Artcloud.auth.keys.cookies.onboardingId, { domain: Artcloud.auth.domain });
  } catch (_) {
    /* best-effort */
  }
};

const _moveToGuardlessOnboardingUrl = () => {
  if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
    window.history.replaceState(null, '', '/onboarding?artist=1');
  }
};

const _firstNameOf = (user) => {
  const full = (user && user.profile && user.profile.fullName) || (user && user.Name) || '';
  return full.trim().split(/\s+/)[0] || '';
};

function Onboarding(props) {
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const _getStepFromLocation = () => {
    let _step = Step.WhoAreYou;
    if (Artcloud && Artcloud.util) {
      _step = Artcloud.util.getparameterbyname('devStep') || _step
    }
    return _step;
  }
  const _hasSession = () => Artcloud.cookie.get(Artcloud.auth.keys.cookies.session)
  const _thresholdPassed = () => Artcloud.cookie.get(Artcloud.auth.keys.cookies.threshold)

  const [testimonials, setTestimonials] = useState([]);
  const _updateTestimonials = (type) => {
    let _type = type || 'Gallery';
    const _testimonials = [];
    for (let key in TestimonialRepository[_type]) {
      _testimonials.push(TestimonialRepository[_type][key]);
    }
    if (_testimonials.length < 2) {
      _testimonials.push(_testimonials[0]);
    }
    setTestimonials(_.shuffle(_testimonials));
  }

  // User Init
  const [step, setStep] = useState(undefined);
  const [user, setUser] = useState({});
  const [convertedRedirectUrl, setConvertedRedirectUrl] = useState(undefined);
  useEffect(() => {
    // A deliberate hand-off from the v2 picker (app-shared.jsx's onPersonaOutsideV2) - it
    // already fired its own 'OB: Persona Selected' beacon (OnboardingV2.tsx's
    // handlePersonaSelect), and _routeFromPersonaSelection is reused here for the routing and the
    // Jewelry coercion, not the event. Checked before _hasSession() on purpose: a ghost session
    // left behind by the gallery arm's step 2 must not veto a choice made after it - vetoing it
    // would strand a Collector/Jewelry pick on the gallery's own profile form (B1). Artist no
    // longer arrives this way since ACA-267 Phase 5 gave v2 its own Artist branch - only Collector
    // and Jewelry still hand off through initialType.
    if (props.initialType && INITIAL_TYPE_TO_USER_TYPE[props.initialType]) {
      _routeFromPersonaSelection(INITIAL_TYPE_TO_USER_TYPE[props.initialType]);
      return;
    }
    if (!_hasSession()) {
      if (props.isArtsy || props.isArtlogic) {
        const typeName = User.Gallery;
        const _user = user;
        _user.Type = { Name: typeName, Value: User.getValue(typeName) };
        setUser(_user);
        setStep(Step.GalleryRegister);
      } else if (props.isArtistReferral) {
        const typeName = User.Artist;
        const _user = user;
        _user.Type = { Name: typeName, Value: User.getValue(typeName) };
        setUser(_user);
        setStep(Step.ArtistRegister);
      } else {
        setStep(Step.WhoAreYou);
      }
      return;
    }
    const _persisted = _loadOnboardingState();
    if (_persisted && _persisted.step) {
      setConvertedRedirectUrl('/');
      if (_persisted.profile || _persisted.email) {
        setUser((prev) => ({ ...prev, profile: _persisted.profile, email: _persisted.email }));
      }
      setStep(_persisted.step);
      setLoading(true);
      Artcloud.api.get(
        Artcloud.url.vendor.onboarding.settings.get(),
        undefined,
        (responseData) => {
          setUser((prev) => ({ ...responseData, profile: prev.profile, email: prev.email ?? responseData?.Email }));
          if (responseData && responseData.Type) {
            _updateTestimonials(responseData.Type.Name);
          }
          setLoading(false);
        },
        onError,
        [Request.header.apiKey()]
      ).always(() => setLoading(false));
      return;
    }
    setLoading(true);
    let _step = _getStepFromLocation();
    switch (_step) {
      case Step.BrandPackagePreview:
      case Step.GetStarted:
        break;
      default:
        _step = props.isArtistReferral && !_thresholdPassed()
          ? Step.Welcome
          : Step.GalleryRegister;
        break;
    }
    setStep(_step);

    Artcloud.api.get(
      Artcloud.url.vendor.onboarding.settings.get(),
      undefined,
      (responseData) => {
        setUser(responseData);
        if (responseData && responseData.Type) {
          _updateTestimonials(responseData.Type.Name);
        }
        setLoading(false);
      },
      onError,
      [Request.header.apiKey()]
    ).always(() => setLoading(false));
  }, []);
  useEffect(() => _updateTestimonials(user && user.Type && user.Type.Name), [TestimonialRepository]);

  // Actions
  const _createAccount = (user, intent, callback) => {
    const postData = Request.appendUtmReferrer({
      DefaultInventoryType: user.DefaultInventoryType ? user.DefaultInventoryType.Value : undefined,
      Intent: intent,
      Type: user.Type && user.Type.Value
    });
    if (props.isArtsy) {
      postData.Source = 0;
    } else if (props.isArtlogic) {
      postData.Source = 1;
    } else if (props.isArtistReferral) {
      postData.Source = 2;
    }
    var session = Artcloud.cookie.get(Artcloud.auth.keys.cookies.session);
    if (session) {
      callback(session);
      return;
    }
    setLoading(true);
    Artcloud.api.post(
      Artcloud.url.mkt.register(),
      postData,
      (responseData) => {
        if (callback) {
          callback(responseData.session);
        }
      },
      onError,
      [Request.header.apiKey()]
    )
  }
  const scrollTop = () => {
    if (document && document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }
  const onError = (jqXHR) => {
    setLoading(false);
    if (jqXHR.responseJSON && jqXHR.responseJSON.Message) {
      setError(jqXHR.responseJSON.Message);
    } else {
      setError('There was an error with your request. Please wait a moment and try again.');
    }
  }
  const _isArtist = () => (user && user.Type && user.Type.Name === User.Artist) || props.isArtistReferral;
  const _isCollector = () => !!(user && user.Type && user.Type.Name === User.Collector);
  const _persistArtistProfile = (profile) => {
    if (!_isArtist() || !profile) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      Artcloud.api.put(
        Artcloud.url.vendor.onboarding.profile.put(),
        {
          FullName: profile.fullName || undefined,
          StudioName: profile.studioName || undefined,
          Phone: profile.phone || undefined,
          CareerStage: profile.careerStage || undefined,
          AnnualSales: profile.annualSales || undefined,
        },
        () => resolve(),
        (jqXHR) => reject(jqXHR),
        [Request.header.apiKey()]
      );
    });
  }
  const _finishArtistOnboarding = () => {
    const goGetStarted = () => {
      _clearOnboardingState();
      Artcloud.cookie.set(Artcloud.auth.keys.cookies.welcome, true);
      scrollTop();
      setStep(Step.GetStarted);
    };
    setLoading(true);
    Artcloud.api.post(
      Artcloud.url.vendor.onboarding.complete.post(),
      undefined,
      () => { setLoading(false); goGetStarted(); },
      () => { setLoading(false); goGetStarted(); },
      [Request.header.apiKey(), Request.header.journey()]
    );
  }
  // The Step.WhoAreYou routing, minus the 'OB: Persona Selected' beacon: shared with the mount
  // effect's props.initialType branch above, which reaches this step already selected and must
  // not re-fire a beacon the v2 picker already sent.
  const _routeFromPersonaSelection = (data) => {
    if (data) {
      const _user = user;
      _user.Type = { Name: data, Value: User.getValue(data) };
      switch (data) {
        case User.Artist:
          _updateTestimonials(data);
          setUser(_user);
          setStep(Step.ArtistRegister);
          return;
        case User.Collector:
          setUser(_user);
          setStep(Step.GalleryRegister);
          return;
        case User.Jewelry:
          _user.DefaultInventoryType = { Value: Inventory.Jewelry };
          _user.Type.Name = User.Gallery;
          break;
      }
      setUser(_user);
    }
    setStep(Step.WhatDoYouWant);
  }
  const onContinue = (data) => {
    switch (step) {
      case Step.WhoAreYou:
        if (data) {
          Artcloud.fullstory.event('OB: Persona Selected', { type: data });
        }
        _routeFromPersonaSelection(data);
        break;
      case Step.WhatDoYouWant:
        if (data) {
          const _user = user;
          _user.Intent = data;
          setUser(_user);

          const intents = [];
          for (let key in Intent.Type) {
            if (data & Intent.Type[key]) {
              intents.push(Intent.displayType(Intent.Type[key]))
            }
          }
          Artcloud.fullstory.event('OB: Intent Declared', { intents });

          if (convertedRedirectUrl) {
            setLoading(true);
            Artcloud.api.put(
              Artcloud.url.vendor.onboarding.settings.put(),
              { Intent: data },
              () => {
                setLoading(false);
                if (_isArtist()) {
                  _finishArtistOnboarding();
                } else {
                  setStep(Step.BrandPackagePreview);
                }
              },
              onError,
              [Request.header.apiKey()]
            );
          } else {
            const next = (session) => {
              Artcloud.auth.setSessionCookie(session);
              setLoading(false);
              setStep(Step.BrandPackagePreview);
            }
            _createAccount(_user, data, next);
          }
        }
        break;
      case Step.BrandPackagePreview:
        if (convertedRedirectUrl) {
          const finishOnboarding = () => {
            _clearOnboardingState();
            Artcloud.cookie.set(Artcloud.auth.keys.cookies.welcome, true);
            window.location = convertedRedirectUrl;
          };
          if (user && user.Type && user.Type.Name === User.Artist) {
            Artcloud.api.post(
              Artcloud.url.vendor.onboarding.complete.post(),
              undefined,
              finishOnboarding,
              finishOnboarding,
              [Request.header.apiKey(), Request.header.journey()]
            );
          } else {
            finishOnboarding();
          }
        } else {
          setStep(Step.GalleryRegister);
        }
        break;
      case Step.GalleryRegister:
        if (!data) {
          break;
        }
        setLoading(true);
        setError(undefined);

        const convert = () => {
          Artcloud.api.post(
            Artcloud.url.vendor.onboarding.conversions.post(),
            data,
            (responseData) => {
              emitAccountConverted();

              function redirectCallback() {
                _clearOnboardingState();
                Artcloud.cookie.set(Artcloud.auth.keys.cookies.welcome, true);
                window.location = responseData.RedirectUrl;
              }

              trackAccountConversion(user.Type.Name, redirectCallback);
            },
            onError,
            [Request.header.apiKey(), Request.header.journey()]
          )
        }
        if (props.isArtsy || props.isArtlogic || _isCollector()) {
          _createAccount(user, undefined, (session) => {
            Artcloud.auth.setSessionCookie(session);
            convert();
          })
        } else {
          convert();
        }
        break;
    }
  };
  return (
    <>
      {
        step === Step.WhoAreYou &&
        <WhoAreYou
          testimonial={testimonials.length > 0 && testimonials[0]}
          onContinue={onContinue}
          onScrollTop={scrollTop} />
      }
      {
        step === Step.WhatDoYouWant && (
          _isArtist()
            ? <IntentScreen
                user={user}
                isArtistReferral={props.isArtistReferral}
                onContinue={onContinue}
                onScrollTop={scrollTop}
                onBack={() => {
                  scrollTop();
                  setStep(Step.Profile);
                }} />
            : <WhatDoYouWant
                testimonial={testimonials.length > 1 && testimonials[1]}
                isArtistReferral={props.isArtistReferral}
                user={user}
                onContinue={onContinue}
                onScrollTop={scrollTop} />
        )}
      {
        step === Step.BrandPackagePreview &&
        <BrandPackagePreview
          error={error}
          user={user}
          onContinue={onContinue}
          onError={onError}
          onScrollTop={scrollTop} />}
      {
        step === Step.ArtistRegister &&
        <div className="wrapper--onboarding wrapper--onboarding--account" data-testid="artist-register-mount">
          <div className="grid--onboarding">
            <div className="brand">
              <Brand isArtsy={false} />
              <Testimonial source={testimonials.length > 0 && testimonials[0]} />
            </div>
            <OnboardingMantineRoot>
              <CreateAccountScreen
                isArtistReferral={props.isArtistReferral}
                onSuccess={(result) => {
                  if (window && window.Artcloud && window.Artcloud.auth && window.Artcloud.auth.setSessionCookie) {
                    window.Artcloud.auth.setSessionCookie(result.sessionToken);
                  }
                  if (window && window.Artcloud && window.Artcloud.fullstory && window.Artcloud.fullstory.event) {
                    window.Artcloud.fullstory.event('OB: Artist Registered', {
                      source: props.isArtistReferral ? 'artlogic-artist' : 'organic',
                    });
                  }
                  if (window && window.gtag) {
                    window.gtag('event', 'conversion', {
                      'send_to': 'AW-988451379/Swi2COyot6MYELOkqtcD',
                    });
                  }
                  setUser({ ...user, email: result.email });
                  setConvertedRedirectUrl('/');
                  setLoading(false);
                  _saveOnboardingState({ step: Step.Welcome, email: result.email });
                  _moveToGuardlessOnboardingUrl();
                  setStep(Step.Welcome);
                }} />
            </OnboardingMantineRoot>
          </div>
        </div>}
      {
        step === Step.Welcome &&
        <div className="wrapper--onboarding wrapper--onboarding--account" data-testid="welcome-mount">
          <div className="grid--onboarding">
            <div className="brand">
              <Brand isArtsy={false} />
              <Testimonial source={testimonials.length > 0 && testimonials[0]} />
            </div>
            <OnboardingMantineRoot>
              <WelcomeScreen
                isArtistReferral={props.isArtistReferral}
                onGetStarted={() => {
                  if (window && window.Artcloud && window.Artcloud.cookie && window.Artcloud.auth) {
                    window.Artcloud.cookie.set(
                      window.Artcloud.auth.keys.cookies.threshold,
                      new Date().getTime(),
                      { expires: 365 });
                  }
                  scrollTop();
                  _saveOnboardingState({ step: Step.Profile });
                  setStep(Step.Profile);
                }} />
            </OnboardingMantineRoot>
          </div>
        </div>}
      {
        step === Step.Profile &&
        <div data-testid="profile-mount">
          <OnboardingMantineRoot>
            <OnboardingProfileScreen
              isArtistReferral={props.isArtistReferral}
              signedInAs={user.Email || user.email}
              initialValues={user.profile}
              onContinue={(values) => {
                return _persistArtistProfile(values).then(() => {
                  setUser({ ...user, profile: values });
                  _saveOnboardingState({ step: ProfileNextStep, profile: values });
                  scrollTop();
                  setStep(ProfileNextStep);
                });
              }}
              onBack={(values) => {
                setUser({ ...user, profile: values });
                _saveOnboardingState({ step: Step.Welcome, profile: values });
                scrollTop();
                setStep(Step.Welcome);
              }} />
          </OnboardingMantineRoot>
        </div>}
      {
        step === Step.GetStarted &&
        <div data-testid="getstarted-mount">
          <GetStartedScreen
            firstName={_firstNameOf(user)}
            signedInAs={user.Email || user.email} />
        </div>}
      {
        step === Step.GalleryRegister &&
        <RegistrationForm
          error={error}
          isArtistReferral={props.isArtistReferral}
          isArtlogic={props.isArtlogic}
          isArtsy={props.isArtsy}
          testimonial={testimonials.length > 2 ? testimonials[2] : testimonials.length > 1 ? testimonials[1] : testimonials[0]}
          user={user}
          onContinue={onContinue}
          onScrollTop={scrollTop} />}
      {loading && <ModalBackground />}
    </>
  );
}

function WhoAreYou(props) {
  const { onContinue } = props;
  const [active, setActive] = useState(undefined);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (Artcloud && Artcloud.util) {
      setActive(Artcloud.util.getparameterbyname('userType'))
    }
  }, [])

  const handleContinue = () => {
    if (props.onScrollTop) {
      props.onScrollTop();
    }
    if (!active) {
      setError(true);
    } else {
      onContinue(active);
    }
  };
  const handleSelect = (value) => {
    setActive(value);
    setError(false);
  }

  return (
    <div className="wrapper--onboarding">
      <div className="grid--onboarding">
        <div className="brand">
          <img className="logo" src="/assets/img/logos/header-mark-left.svg" />
          <Testimonial source={props.testimonial} />
        </div>
        <div className="form-header">
          <h1>Welcome to ArtCloud. Tell us about yourself.</h1>
          <p>Choose whatever fits you best</p>
        </div>
        <div className="form-body">
          {error && (
            <div className="form-error">
              <p>Please select an option to continue</p>
            </div>
          )}
          <div className="form-body__grid--boxes form-body__grid--boxes--4">
            <div
              className={`box box--center ${active === User.Gallery ? "box--active" : ""
                }`}
              onClick={() => handleSelect(User.Gallery)}
            >
              <img
                src="/assets/img/onboarding/gallery.svg"
                height="60"
                width="auto"
              />
              <h2>Gallery</h2>
            </div>
            <div
              className={`box box--center ${active === User.Artist ? "box--active" : ""
                }`}
              onClick={() => handleSelect(User.Artist)}
            >
              <img
                src="/assets/img/onboarding/artist.svg"
                height="60"
                width="auto"
              />
              <h2>Artist</h2>
            </div>
            <div
              className={`box box--center ${active === User.Collector ? "box--active" : ""
                }`}
              onClick={() => handleSelect(User.Collector)}
            >
              <img
                src="/assets/img/onboarding/collector.svg"
                height="60"
                width="auto"
              />
              <h2>Collector</h2>
            </div>
            <div
              className={`box box--center ${active === User.Jewelry ? "box--active" : ""
                }`}
              onClick={() => handleSelect(User.Jewelry)}
            >
              <img
                src="/assets/img/onboarding/jewelry.svg"
                height="60"
                width="auto"
              />
              <h2>Jewelry</h2>
            </div>
          </div>
          <div className="form-body__footer">
            <MantineButton size="lg" onClick={handleContinue}>
              Continue
            </MantineButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// The non-artist intent step. Only galleries reach it: Collector goes straight
// to the registration form and Jewelry is coerced to Gallery, both in
// onContinue's WhoAreYou case.
function WhatDoYouWant(props) {
  const options = galleryIntents(User.getTypeDisplay(props.user || {}).toLowerCase());
  const { intent, error, toggle, submit } = useIntentSelection({
    options,
    isArtistReferral: props.isArtistReferral,
    onContinue: props.onContinue,
    onScrollTop: props.onScrollTop,
  });

  return (
    <div className="wrapper--onboarding">
      <div className="grid--onboarding">
        <div className="brand">
          <Brand />
          <Testimonial source={props.testimonial} />
        </div>
        <div className="form-header">
          <h1>What do you want to do on ArtCloud?</h1>
          <p>We'll personalise your Getting Started checklist around these.</p>
        </div>
        <div className="form-body">
          {error && (
            <div className="form-error">
              <p>Please select at least one option to continue</p>
            </div>
          )}
          <div className="intent-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {options.map((p) => {
              const selected = (intent & p.value) !== 0;
              return (
                <button
                  key={p.value}
                  type="button"
                  aria-pressed={selected}
                  data-testid={`intent-chip-${p.value}`}
                  data-selected={selected ? 'true' : 'false'}
                  onClick={() => toggle(p.value)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '12px 18px',
                    borderRadius: 999,
                    border: `1px solid ${selected ? '#00AEFF' : '#e3e1de'}`,
                    background: selected ? 'rgba(0, 177, 255, 0.10)' : '#f5f3f0',
                    color: '#1a1a1a',
                    fontSize: 15.5,
                    fontWeight: 500,
                    lineHeight: 1,
                    cursor: 'pointer',
                    transition: 'border-color 140ms ease, background 140ms ease',
                  }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="form-body__footer">
            <p className="form-btn-note">On the next screen, you can test out our most popular features before you submit your account information.</p>
            <MantineButton size="lg" onClick={submit}>Preview&nbsp;ArtCloud</MantineButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function EnumSelect(props) {
  const [value, setValue] = useState(props.value);
  useEffect(() => {
    setValue(props.value);
  }, [props.value]);
  const selectedOption = props.type.options.find((o) => o.value === value);
  const handleSelect = (name, item) => {
    const next = item ? item.value : undefined;
    setValue(next);
    if (props.onChange) {
      props.onChange(props.fieldName, next);
    }
  };
  return (
    <div className="input-wrapper">
      <label>{props.label}</label>
      <DropDown
        name={props.fieldName}
        value={selectedOption ? selectedOption.display : props.placeholder}
        options={props.type.options}
        onSelect={handleSelect} />
    </div>
  );
}

function RegistrationForm(props) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let _data = data || {};
    if (props.user.Type != undefined) {
      _data = { ..._data, ...props.user }
    }
    if (Artcloud && Artcloud.util) {
      const paramMap = { email: 'Email', name: 'Name', businessName: 'BusinessName', website: 'Website', phone: 'Phone' };
      for (const [param, field] of Object.entries(paramMap)) {
        const val = Artcloud.util.getparameterbyname(param);
        if (val) {
          _data[field] = _data[field] || val;
        }
      }
    }
    setData(_data);
  }, [props.user.Type])

  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (props.error && props.error.length > 0) {
      setError(props.error);
    }
  }, [props.error])
  const isArtist = props.user && props.user.Type && props.user.Type.Name === User.Artist;
  const isCollector = props.user && props.user.Type && props.user.Type.Name === User.Collector;
  const handleChange = (name, value) => {
    let _value = value;
    switch (name) {
      case 'HasAgreedToTerms':
        _value = !data.HasAgreedToTerms ? true : false;
        break;
    }
    const _data = data;
    data[name] = _value;
    setData(_data);
  }
  const handleContinue = () => {
    if (props.onScrollTop) {
      props.onScrollTop();
    }
    if (!data.Email || data.Email.length == 0) {
      setError('Please enter your email address.')
      return;
    }
    if (!data.Password || data.Password.length == 0) {
      setError('Please provide a password.')
      return;
    }
    if (!data.HasAgreedToTerms) {
      setError('Please accept the terms of service.');
      return;
    }
    if (isArtist && !data.CareerStage) {
      setError('Please select your career stage.');
      return;
    }
    if (isArtist && !data.AnnualSalesBracket) {
      setError('Please select your annual sales range.');
      return;
    }
    props.onContinue(data);
  };
  const typeDisplay = props.user && props.user.Type != undefined ? User.getTypeDisplay(props.user) : '';
  let header;
  let style = {};
  if (props.isArtlogic) {
    header = (
      <>
        <h1>Get Started with ArtCloud by Artlogic</h1>
        <p>CREATE YOUR ACCOUNT AND TRY THE 14-DAY FREE TRIAL</p><br />
        <p>Based on your needs, ArtCloud by Artlogic will be a perfect fit for your gallery. ArtCloud offers powerful streamlined tools to help you manage inventory, sales, website and client relationships with ease.</p>
      </>
    );
    style = { 'gridTemplateRows': '210px auto' };
  } else if (props.isArtsy) {
    header = (
      <>
        <h1>Get started with Artsy x ArtCloud</h1>
        <p>Create an account</p>
      </>
    );
  } else if (isCollector) {
    header = (
      <>
        <h1>Sign up for a free account today to get started.</h1>
        <p>ArtCloud by Artlogic offers streamlined tools to help you catalog your collection, track provenance, and keep purchase records in one place.</p>
      </>
    );
  } else {
    header = (
      <>
        <h1>Sign up for a free account today to get started.</h1>
        <p>Free accounts have limited access to inventory management tools. To experience everything ArtCloud by Artlogic has to offer, you can sign up for a 14-day free trial.</p>
      </>
    );
  }
  return (
    <div className="wrapper--onboarding">
      <div className="grid--onboarding" style={style}>
        <div className="brand">
          <Brand isArtsy={props.isArtsy} />
          {!isCollector && <Testimonial source={props.testimonial} />}
        </div>
        <div className="form-header">
          {header}
        </div>
        {
          data &&
          <div className="form-body">
            {error != undefined && (
              <div className="form-error">
                <p>{error}</p>
              </div>
            )}
            <div className="form-body__grid--boxes form-body__grid--boxes--2">
              <div className="input-wrapper">
                <label>Email</label>
                <Input
                  name="Email"
                  required={true}
                  type="email"
                  value={data.Email}
                  onChange={handleChange} />
              </div>
              <div className="input-wrapper">
                <label>Full Name</label>
                <Input
                  name="Name"
                  type="text"
                  value={data.Name}
                  onChange={handleChange} />
              </div>
              {
                !isCollector &&
                <div className="input-wrapper">
                  <label>{typeDisplay} Name</label>
                  <Input
                    name="BusinessName"
                    type="text"
                    value={data.BusinessName}
                    onChange={handleChange} />
                </div>
              }
              {
                !isCollector &&
                <div className="input-wrapper">
                  <label>{typeDisplay} Website</label>
                  <Input
                    name="Website"
                    type="url"
                    value={data.Website}
                    onChange={handleChange} />
                </div>
              }
              <div className="input-wrapper">
                <label>Password</label>
                <Input
                  name="Password"
                  type="password"
                  value={data.Password}
                  onChange={handleChange} />
              </div>
              <div className="input-wrapper">
                <label>Phone Number</label>
                <Input
                  name="Phone"
                  type="tel"
                  value={data.Phone}
                  onChange={handleChange} />
              </div>
              {
                isArtist &&
                <EnumSelect
                  fieldName="CareerStage"
                  label="Career Stage"
                  onChange={handleChange}
                  placeholder="Select your career stage..."
                  type={CareerStage}
                  value={data.CareerStage} />
              }
              {
                isArtist &&
                <EnumSelect
                  fieldName="AnnualSalesBracket"
                  label="Annual Sales"
                  onChange={handleChange}
                  placeholder="Select your annual sales range..."
                  type={AnnualSalesBracket}
                  value={data.AnnualSalesBracket} />
              }
              {
                !props.isArtistReferral &&
                !props.isArtlogic &&
                !props.isArtsy &&
                <div className="input-wrapper">
                  <label>Where did you hear about us?</label>
                  <Input
                    name="HowDidYouHear"
                    type="text"
                    value={data.HowDidYouHear}
                    onChange={handleChange} />
                </div>
              }
            </div>
            {/* <div className="subscription">
            <img className="logo" src="/assets/img/onboarding/curate.png" />
            <div>
              <p className="header">Must-have news for every gallerist</p>
              <p className="messaging">
                A weekly newsletter of industry news, interviews, and more.
              </p>
              <div className="subscribe">
                <label className="switch">
                  <input type="checkbox" />
                  <span className="slider round"></span>
                </label>
                <p>
                  Subscribe to <span>Curate</span> by Artcloud
                </p>
              </div>
            </div>
          </div> */}
            <div className="form-body__footer gap--24">
              <Checkbox
                display={<>Accept <a href={termsOfServiceUrl()} target="_blank">terms of service</a></>}
                name="HasAgreedToTerms"
                value={data.HasAgreedToTerms == true}
                onChange={handleChange} />
              <MantineButton size="lg" onClick={handleContinue}>
                Create Account
              </MantineButton>
            </div>
          </div>
        }
      </div>
    </div>
  );
}

const maxUploads = 10;
function UploadArtwork(props) {
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState(props.inventory && props.inventory.length > 0 ? [...props.inventory] : undefined);
  const inventoryRef = React.useRef()
  inventoryRef.current = inventory;
  const [timestamp, setTimestamp] = useState(new Date().getTime());

  const handleFileRead = (event) => {
    if (!event.target || !event.target.files || event.target.files.length == 0) {
      return;
    }
    const errors = [];
    let validFiles = [];
    let file;
    for (let i = 0; i < event.target.files.length; i++) {
      file = event.target.files[i];
      if (file.size > 20971520) {
        errors.push(file.name);
      } else {
        validFiles.push(file);
      }
    }
    if (errors.length > 0) {
      setError(<div style={{ color: 'red' }}>Maximum upload size is 20MB:<ul style={{ listStyle: 'none' }}>{errors.map(d => <li>{d}</li>)}</ul></div>)
    }
    if (validFiles.length > 0) {
      let _inventory = inventoryRef.current;
      if (_inventory == undefined) {
        _inventory = [];
        setInventory([]);
      }
      if (_inventory.length + validFiles.length > maxUploads) {
        validFiles = validFiles.slice(0, maxUploads - _inventory.length)
      }
      setLoading(true);
      handleAdd(0, validFiles);

      Artcloud.fullstory.event('OB: Inventory Uploaded', { count: validFiles.length });
    }
  }
  const handleAdd = (index, files) => {
    if (index >= files.length) {
      setLoading(false);
      return;
    }
    const file = files[index]
    const postData = { DateCreated: `${new Date().getFullYear()}` }
    postData.Title = file.name;
    if (postData.Title && postData.Title.lastIndexOf) {
      const _index = postData.Title.lastIndexOf('.');
      if (_index > 0) {
        postData.Title = postData.Title.substring(0, _index);
      }
    }
    Artcloud.api.post(
      Artcloud.url.vendor.inventory.post(),
      postData,
      (item) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          Artcloud.api.post(
            Artcloud.url.vendor.inventory.images.post(item.InventoryId),
            { Content: reader.result, Filename: file.name },
            (imageData) => {
              const _inventory = inventoryRef.current;
              item.Image = imageData;
              _inventory.push(item)
              setInventory(_inventory);
              setTimeout(() => handleAdd(index + 1, files), 0);
            },
            props.onError
          )
        }
        reader.readAsDataURL(file);
      },
      props.onError
    )
  }
  const updateItem = (item) => {
    const _item = { ...item };
    _item.ArtistId = _item.Artist && _item.Artist.ArtistId;
    Artcloud.api.put(Artcloud.url.vendor.inventory.items.put(item.InventoryId, item.ItemId || item.EditionId), _item)
  }
  const _handleChange = _.debounce(updateItem, 1500);
  const handleChange = (index, name, value) => {
    if (index >= inventoryRef.current.length) {
      return;
    }
    const _inventory = inventoryRef.current;
    const item = _inventory[index]
    switch (name) {
      case 'Artist':
        if (item.Artist && item.Artist.ArtistId == value.value) {
          // Same artist selected
          return;
        }
        if (value.isNew) {
          const postData = {};
          const components = value.display.split(' ');
          if (components.length == 1) {
            postData.FirstName = components[0];
          } else if (components.length > 0) {
            postData.LastName = components[components.length - 1];
            components.splice(components.length - 1, 1);
            postData.FirstName = components.join(' ');
          }
          Artcloud.api.post(
            Artcloud.url.vendor.artists.post(),
            postData,
            (responseData) => {
              let display = responseData.FirstName;
              if (responseData.LastName && responseData.LastName.length > 0) {
                display = `${display} ${responseData.LastName}`;
              }
              handleChange(index, name, { display, value: responseData.ArtistId });
            },
            props.onError
          );
          return;
        }
        item[name] = { ArtistId: value.value, Name: value.display };
        break;
      default:
        item[name] = value;
        break;
    }
    setInventory(_inventory);
    _handleChange(item);
  }
  const handleContinue = () => {
    if (!inventoryRef.current || inventoryRef.current.length == 0) {
      props.onClose();
      return;
    }
    setLoading(true);
    const addItemsToList = (listId) => {
      const Items = inventoryRef.current.map(d => ({ ItemId: d.ItemId || d.EditionId }));
      Artcloud.api.post(Artcloud.url.vendor.lists.items.post(listId), { Items }, () => {
        props.onContinue({ inventory: inventoryRef.current, listId })
        props.onClose();
      }, () => setLoading(false))
    }
    if (props.listId) {
      addItemsToList(props.listId);
      return;
    }
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 3);
    Artcloud.api.post(
      Artcloud.url.vendor.lists.post(),
      {
        About: `<p>Step into the captivating world of \"Sample Exhibition,\" an exhibition that celebrates the diverse expressions of contemporary artists from around the globe. This carefully curated showcase invites you to explore a collection of thought-provoking artworks that challenge conventional boundaries and ignite the imagination.</p><p>&nbsp;</p><p>With an innovative blend of traditional and modern mediums, \"Sample Exhibition\" presents a dynamic fusion of painting, sculpture, multimedia installations, and immersive experiences. From abstract compositions that evoke emotions to conceptual works that spark intellectual contemplation, each piece offers a unique perspective on the human experience and the world we inhabit.</p>`,
        EndDate: endDate.toLocaleDateString('en-US'),
        StartDate: startDate.toLocaleDateString('en-US'),
        Title: 'Sample Exhibition',
        Type: 1
      },
      (responseData) => {
        if (!responseData.ListId) {
          return;
        }
        addItemsToList(responseData.ListId);
      },
      () => setLoading(false)
    )
  }
  const handleParseSearchData = (name, data) => {
    const parsed = [];
    switch (name) {
      case 'Artist':
      case 'Medium':
        data.Data.forEach(d => parsed.push({ display: d.Display, value: d.Value }))
        break;
    }
    return parsed;
  }
  const handleSelect = (index, name, data) => {
    let value = data.value;
    switch (name) {
      case 'Artist':
        value = data;
        break;
    }
    handleChange(index, name, value);
  }
  const handleUseForAllItems = (index, name, data) => {
    if (!inventoryRef.current || inventoryRef.current.length == 1) {
      return;
    }
    const _inventory = inventoryRef.current;
    const itemProp = data[name];
    if (!itemProp) {
      return;
    }
    for (let i = 0; i < _inventory.length; i++) {
      if (i == index) {
        continue;
      }
      _inventory[i][name] = itemProp;
      updateItem(_inventory[i]);
    }
    setInventory(_inventory);
    setTimestamp(new Date().getTime())
  }
  const cannotAddInventory = inventory != undefined && inventory.length >= maxUploads
  const isArtist = props.user && props.user.Type && props.user.Type.Name == User.Artist;
  return (
    <div className={`upload${inventory != undefined ? ' upload--with__inventory' : ''}`}>
      <CloseButton onClick={handleContinue} />
      {
        inventory == undefined &&
        <>
          <h1>Upload inventory</h1>
          <p>Drop in a few pieces to get started</p>
        </>
      }
      {error != undefined && (
        <div className="form-error">
          <p>{error}</p>
        </div>
      )}
      <div className="upload-file" style={cannotAddInventory ? undefined : { cursor: 'pointer' }}>
        {
          cannotAddInventory ?
            <p>
              Only {maxUploads} inventory uploads are allowed in Preview mode
            </p>
            :
            <>
              <img src="/assets/img/onboarding/upload-artwork.svg" />
              <input
                id="artwork"
                accept="image/png, image/jpg, image/jpeg, image/bmp, image/svg+xml, image/gif"
                multiple={true}
                title=""
                type="file"
                onChange={handleFileRead} />
              <p>
                <label htmlFor="artwork">Upload</label> or drag images here
              </p>
            </>
        }
      </div>
      {
        inventory != undefined &&
        inventory.length > 0 &&
        <div className="grid--onboarding" style={{ gridGap: '0', gridTemplateColumns: 'unset', margin: '80px auto 120px', maxWidth: '1200px' }}>
          <div className="form-body">
            {
              inventory.map((d, index) => {
                const _handleChange = (name, value) => handleChange(index, name, value);
                const _handleDropDown = (name, data) => {
                  _handleChange(name, data.value);
                  setTimestamp(new Date().getTime())
                }
                const _handleSelect = (name, data) => handleSelect(index, name, data);
                const _handleUseForAllItems = (name) => handleUseForAllItems(index, name, d);
                const metricOptions = [Metric.Centimeters, Metric.Inches, Metric.Feet].map(m => ({ display: Metric.getTypeDisplay(m), value: m }));
                return (
                  <div key={d.InventoryId} className="form-body__grid--boxes form-body__grid--boxes--3" style={{ marginBottom: '60px' }}>
                    <div style={{ display: 'none' }}>{timestamp}</div>
                    <div>
                      <img src={d.Image && d.Image.MediumUrl} />
                    </div>
                    <div>
                      {
                        isArtist ?
                          <div className="input-wrapper" style={{ marginBottom: '15px' }}>
                            <label>Title</label>
                            <Input
                              name="Title"
                              type="text"
                              value={d.Title}
                              onChange={_handleChange} />
                          </div>
                          :
                          <div className="search-wrapper" style={{ marginBottom: '15px' }}>
                            <SearchableDropDown
                              label={<LabelWithUseAll name="Artist" onUseAll={_handleUseForAllItems}>Artist</LabelWithUseAll>}
                              name="Artist"
                              query={{ context: 0, dir: 0, order: 0 }}
                              route={Artcloud.url.vendor.tags.get(5)}
                              value={d.Artist && d.Artist.Name}
                              onError={props.onError}
                              onParseData={handleParseSearchData}
                              onSelect={_handleSelect} />
                          </div>
                      }
                      <div className="search-wrapper" style={{ marginBottom: '15px' }}>
                        <SearchableDropDown
                          label={<LabelWithUseAll name="Medium" onUseAll={_handleUseForAllItems}>Medium</LabelWithUseAll>}
                          name="Medium"
                          query={{ context: 0, dir: 0, order: 0 }}
                          route={Artcloud.url.vendor.tags.get(6)}
                          value={d.Medium}
                          onError={props.onError}
                          onParseData={handleParseSearchData}
                          onSelect={_handleSelect} />
                      </div>
                      <div className="form-body__grid--boxes form-body__grid--boxes--3" style={{ marginBottom: '15px' }}>
                        <div className="input-wrapper">
                          <label>Height</label>
                          <Input
                            name="Height"
                            style={{ width: '100%' }}
                            type="number"
                            value={d.Height}
                            onChange={_handleChange} />
                        </div>
                        <div className="input-wrapper">
                          <label>Width</label>
                          <Input
                            name="Width"
                            style={{ width: '100%' }}
                            type="number"
                            value={d.Width}
                            onChange={_handleChange} />
                        </div>
                        <div className="input-wrapper">
                          <label>Depth</label>
                          <Input
                            name="Depth"
                            style={{ width: '100%' }}
                            type="number"
                            value={d.Depth}
                            onChange={_handleChange} />
                        </div>
                      </div>
                    </div>
                    <div>
                      {
                        isArtist ?
                          <div className="input-wrapper" style={{ height: '74.5px', marginBottom: '15px' }} />
                          :
                          <div className="input-wrapper" style={{ marginBottom: '15px' }}>
                            <label>Title</label>
                            <Input
                              name="Title"
                              type="text"
                              value={d.Title}
                              onChange={_handleChange} />
                          </div>
                      }
                      <div className="input-wrapper" style={{ marginBottom: '15px' }}>
                        <label>Price</label>
                        <Input
                          name="Price"
                          type="number"
                          value={d.Price}
                          onChange={_handleChange} />
                      </div>
                      <div className="input-wrapper" style={{ marginBottom: '15px' }}>
                        <label>Metric</label>
                        <DropDown
                          name="Metric"
                          options={metricOptions}
                          value={Metric.getTypeDisplay(d.Metric)}
                          onSelect={_handleDropDown} />
                      </div>
                    </div>
                  </div>
                )
              }
              )
            }
            <div style={{ height: '60px' }} />
          </div>
          <div style={{ background: 'linear-gradient(180deg, transparent, white 43%)', bottom: 0, display: 'flex', flexDirection: 'column', height: '120px', justifyContent: 'end', maxWidth: '1200px', padding: '0 20px 10px', position: 'fixed', width: '100%' }}>
            <MantineButton size="lg" onClick={handleContinue}>
              Continue
            </MantineButton>
          </div>
        </div>
      }
      {
        loading &&
        <ModalBackground />
      }
    </div>
  );
}

const collateralPreviewStyles = `
.pageContainer {margin-bottom:20px;min-height:11.5in;position:relative;}
.pageContainer:after {background-color:#414141;content:'';height:20px;left:0;position:absolute;top:100%;width:100%;}
.pageContainer:last-child {margin-bottom:0;}
.pageContainer:last-child:after {content:none;}
.pageHeader, .pageHeader>td {display:block;}`;

function BrandPackagePreview(props) {
  const [activeView, setActiveView] = useState(Preview.Type.Website);
  const [controlsClosed, setControlsClosed] = useState(false);

  // Sample Set
  const [sampleSetOptions] = React.useState(_.shuffle(Artcloud.dataSub.gallery.options()));
  const [sampleSet, setSampleSet] = React.useState(undefined);
  const handleSampleSetSelect = (event) => {
    if (!event || !event.currentTarget || !event.currentTarget.selectedOptions || event.currentTarget.selectedOptions.length == 0) {
      return;
    }
    const value = event.currentTarget.selectedOptions[0].value;
    if (!value || value == sampleSet) {
      return;
    }
    setSampleSet(value);
  }
  React.useEffect(() => {
    if (!sampleSet) {
      return;
    }
    getInventoryData({});
  }, [sampleSet])

  const isArtist = () => (props.user && props.user.Type && props.user.Type.Name == User.Artist) || (websiteSettings && websiteSettings.Type && websiteSettings.Type.Name == User.Artist);
  const getDataSub = () => {
    if (isArtist()) {
      return Artcloud.dataSub.artist;
    } else {
      return sampleSet || Artcloud.dataSub.gallery.baroque;
    }
  }

  // Error Handling
  const [error, setError] = useState(undefined);
  const clearError = () => setError(undefined);
  useEffect(() => {
    if (props.error && props.error.length > 0) {
      setError(props.error);
    }
  }, [props.error])
  const handleError = (jqXHR, textStatus) => {
    setLoadingLogo(false);
    setSaving(false);
    setShowLoading(false);
    props.onError(jqXHR, textStatus);
  }

  // Data Initialization
  const [data, setData] = useState(undefined);
  const dataRef = React.useRef();
  dataRef.current = data;
  const [websiteSettings, setWebsiteSettings] = useState({});
  const getCollateralData = () => {
    Artcloud.api.get(
      Artcloud.url.vendor.collaterals.data.shared.get(),
      undefined,
      getInventoryData,
      handleError
    )
  }
  const getInventoryData = (shared) => {
    const getSamples = (dataSub) => {
      Artcloud.api.get(
        Artcloud.url.vendor.onboarding.samples.get(dataSub),
        undefined,
        (sampleData) => {
          for (let key in sampleData) {
            shared[key] = sampleData[key]
          }
          const _data = dataRef.current ? { ...dataRef.current, ...shared } : shared;
          setData(_data);
          refreshCollateralPreview();
          refreshCampaignPreview();
        },
        handleError,
        [Request.header.apiKey()]
      )
    }
    if (inventoryRef.current && inventoryRef.current.length > 0) {
      getSamples()
    } else {
      Artcloud.api.get(
        Artcloud.url.vendor.onboarding.inventory.get(),
        undefined,
        (inventoryResponseData) => {
          let dataSub = getDataSub();
          if (inventoryResponseData.Data && inventoryResponseData.Data.length > 0) {
            setInventory(inventoryResponseData.Data);
            dataSub = undefined;
          }
          getSamples(dataSub);
          if (dataRef.current != undefined && websiteSettings) {
            updateWebsitePreviewUrl(websiteSettings.Subdomain);
            refreshWebsitePreview();
          }
        },
        handleError,
        [Request.header.apiKey()]
      )
    }
  }
  useEffect(() => {
    Artcloud.api.get(
      Artcloud.url.vendor.websites.settings.get(),
      undefined,
      setWebsiteSettings,
      handleError
    );
  }, []);
  useEffect(() => updateWebsitePreviewUrl(websiteSettings.Subdomain), [websiteSettings.Subdomain])
  useEffect(() => {
    if (!props.user.Type) {
      return;
    }
    getCollateralData()
  }, [props.user.Type])

  // Selection
  const [brandPackageSelectionVisible, setBrandPackageSelectionVisible] = useState(false);
  const [packageConfigs, setPackageConfigs] = useState({});
  const [selectedPackage, setSelectedPackage] = useState({});
  const [websitePreviewUrl, setWebsitePreviewUrl] = useState(undefined);
  const handleSelectCollateralTemplate = (pkg, type, callback) => {
    setLoadingCollateralPreview(true);
    if (packageConfigs[pkg.BrandPackageId] && packageConfigs[pkg.BrandPackageId].Collateral && packageConfigs[pkg.BrandPackageId].Collateral.Configs && packageConfigs[pkg.BrandPackageId].Collateral.Configs.length > 0) {
      const templateConfigs = packageConfigs[pkg.BrandPackageId].Collateral.Configs;
      if (type != undefined) {
        for (let i = 0; i < templateConfigs.length; i++) {
          if (!templateConfigs[i].Type || templateConfigs[i].Type.Value != type) {
            continue;
          }
          setCollateralTemplate(templateConfigs[i]);
          break;
        }
      } else {
        setCollateralTemplate(templateConfigs[0]);
      }
      if (callback) {
        callback();
      }
    } else {
      setLoadingCollateralPreview(false);
    }
  }
  const handleSelect = (pkg) => {
    if (!pkg || pkg.BrandPackageId == selectedPackage.BrandPackageId) {
      return;
    }
    setLoadingWebsitePreview(true);
    updateWebsitePreviewUrl(websiteSettings.Subdomain);

    Artcloud.api.post(
      Artcloud.url.vendor.brandPackages.configs.post(pkg.BrandPackageId),
      undefined,
      () => {
        if (activeView == Preview.Type.Website) {
          setShowLoading(true);
        }
        setLoadingWebsitePreview(false);
      },
      (jqXHR, textStatus, errorThrown) => {
        setLoadingWebsitePreview(false);
        props.onError(jqXHR, textStatus, errorThrown);
      }
    )
    setCampaignConfig(undefined);
    setCollateralTemplate({});
    setSelectedPackage(pkg);

    const callback = () => {
      handleSelectCollateralTemplate(pkg, Preview.CollateralType.valueOf(collateralType));
      if (packageConfigs[pkg.BrandPackageId] && packageConfigs[pkg.BrandPackageId].Campaign && packageConfigs[pkg.BrandPackageId].Campaign.Config) {
        setCampaignConfig(EditorTool.getDeepCopy(packageConfigs[pkg.BrandPackageId].Campaign.Config));
      }
      setLoadingBrandPackageConfig(false);
    }
    setLoadingBrandPackageConfig(true);
    if (!packageConfigs[pkg.BrandPackageId]) {
      Artcloud.api.get(
        Artcloud.url.vendor.brandPackages.configs.get(pkg.BrandPackageId),
        { isPublished: 1 },
        (responseData) => {
          const _packageConfigs = packageConfigs;
          if (responseData.Website &&
            responseData.Website.Styleguide &&
            responseData.Website.Styleguide.Documents &&
            responseData.Website.Styleguide.Documents.Kit &&
            responseData.Website.Styleguide.Documents.Kit.global) {
            if (!responseData.Website.Styleguide.Fonts) {
              responseData.Website.Styleguide.Fonts = {};
            }
            if (responseData.Website.Styleguide.Documents.Kit.global.h1 && responseData.Website.Styleguide.Documents.Kit.global.h1.design) {
              responseData.Website.Styleguide.Fonts.Header = extractFont(responseData.Website.Styleguide.Documents.Kit.global.h1.design);
              setHeaderFont(responseData.Website.Styleguide.Fonts.Header);
            }
            if (responseData.Website.Styleguide.Documents.Kit.global.p && responseData.Website.Styleguide.Documents.Kit.global.p.design) {
              responseData.Website.Styleguide.Fonts.Body = extractFont(responseData.Website.Styleguide.Documents.Kit.global.p.design);
              setBodyFont(responseData.Website.Styleguide.Fonts.Body);
            }
          }
          _packageConfigs[pkg.BrandPackageId] = responseData;
          setPackageConfigs(_packageConfigs);
          callback();
        },
        (jqXHR, textStatus, errorThrown) => {
          setLoadingBrandPackageConfig(false);
          props.onError(jqXHR, textStatus, errorThrown);
        }
      )
    } else {
      setTimeout(callback, 100);
    }
  }
  const updateWebsitePreviewUrl = (subdomain) => {
    if (!subdomain) {
      return;
    }
    const baseUrl = urls && urls.Click || 'https://click.artcloud.com'
    let _websitePreviewUrl = `${baseUrl}/?sub=${subdomain}`
    if (!inventoryRef.current || inventoryRef.current.length == 0) {
      _websitePreviewUrl += `&dataSub=${getDataSub()}`
    }
    setWebsitePreviewUrl(_websitePreviewUrl);
  }

  // Package Initialization
  const [cannedFonts, setCannedFonts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [urls, setUrls] = useState({});
  useEffect(() => {
    if (!props.user.Type) {
      return;
    }
    const accountType = props.user.Type.Name == User.Artist ? 2 : 1;
    Artcloud.api.get(
      Artcloud.url.vendor.brandPackages.get(),
      { accountType, isPublished: 1 },
      (responseData) => {
        setCannedFonts(responseData.Fonts && responseData.Fonts.Families);
        setUrls(responseData.Urls);
        setPackages(_.shuffle(responseData.Data));
      },
      (jqXHR, textStatus, errorThrown) => {
        setPackages([]);
        props.onError(jqXHR, textStatus, errorThrown);
      }
    )
  }, [props.user.Type]);
  useEffect(() => {
    if (!selectedPackage.BrandPackageId && packages.length > 0) {
      handleSelect(packages[0]);
    }
  }, [packages.length])

  // Preview
  const [campaignConfig, setCampaignConfig] = useState(undefined);
  const [collateralTemplate, setCollateralTemplate] = useState({});
  const [collateralType, setCollateralType] = useState(Preview.CollateralType.Tearsheet);
  const [loadingBrandPackageConfig, setLoadingBrandPackageConfig] = useState(false);
  const [loadingCollateralPreview, setLoadingCollateralPreview] = useState(false);
  const [loadingWebsitePreview, setLoadingWebsitePreview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const findCampaignBlock = (_campaignConfig, type) => {
    if (!_campaignConfig || !_campaignConfig.blocks) {
      return;
    }
    for (let i = 0; i < _campaignConfig.blocks.length; i++) {
      if (!_campaignConfig.blocks[i].type || _campaignConfig.blocks[i].type.value != type) {
        continue;
      }
      return _campaignConfig.blocks[i];
    }
  }
  const handleCollateralPreviewLoad = (frameDoc) => { setLoadingCollateralPreview(false) }
  const handleCollateralTemplateTypeChange = (value) => {
    setLoadingCollateralPreview(true);
    setCollateralType(value);
    setTimeout(() => {
      handleSelectCollateralTemplate(selectedPackage, Preview.CollateralType.valueOf(value), () => {
        setTimeout(() => setLoadingCollateralPreview(false), 100);
      });
      const elements = document.getElementsByClassName('package__preview--cont__collateral');
      if (!elements || elements.length == 0 || !elements[0].children || elements[0].children.length == 0 || !elements[0].children[0].contentDocument || !elements[0].children[0].contentDocument.scrollingElement) {
        return;
      }
      elements[0].children[0].contentDocument.scrollingElement.scrollTop = 0
    }, 500);
  }
  const refreshCampaignPreview = () => {
    if (activeView == Preview.Type.Email) {
      // TODO
      // const temp = { ...collateralTemplate };
      // setCollateralTemplate({});
      // setLoadingCollateralPreview(true);
      // setTimeout(() => setCollateralTemplate(temp), 500);
    }
  }

  const [refreshCollateralPreviewTimeoutHandle, setRefreshCollateralPreviewTimeoutHandle] = React.useState(undefined);
  const refreshCollateralPreview = () => {
    if (activeView == Preview.Type.Collateral) {
      if (!collateralTemplate.CollateralTemplateId) {
        if (!refreshCollateralPreviewTimeoutHandle) {
          const handle = setTimeout(refreshCollateralPreview, 500);
          setRefreshCollateralPreviewTimeoutHandle(handle);
        }
        return;
      }
      if (refreshCollateralPreviewTimeoutHandle) {
        clearTimeout(refreshCollateralPreviewTimeoutHandle);
        setRefreshCollateralPreviewTimeoutHandle(undefined);
      }
      const temp = { ...collateralTemplate };
      setCollateralTemplate({});
      setLoadingCollateralPreview(true);
      setTimeout(() => setCollateralTemplate(temp), 500);
    }
  }
  const refreshWebsitePreview = () => {
    if (activeView == Preview.Type.Website) {
      setLoadingWebsitePreview(true);
      setTimeout(() => setLoadingWebsitePreview(false), 500);
    }
  }

  // Business Name
  const [businessName, setBusinessName] = useState(undefined);
  const _updateBusinessName = (value) => {
    setBusinessName(value);
    if (!dataRef.current || !dataRef.current.Gallery) {
      return;
    }
    const _data = dataRef.current;
    for (let key in _data) {
      if (!_data[key].Gallery) {
        continue;
      }
      _data[key].Gallery.Company = value;
    }
    setData(_data);
    props.user.BusinessName = value;
  }
  useEffect(() => {
    if (props.user.Type == undefined) {
      return;
    }
    let name = props.user.BusinessName;
    if (!name && Artcloud && Artcloud.util) {
      name = Artcloud.util.getparameterbyname('businessName');
    }
    _updateBusinessName(name);
  }, [props.user.Type])
  const handleBusinessNameChange = (_, value) => setBusinessName(value);
  const handleBusinessNameBlur = () => {
    Artcloud.api.put(
      Artcloud.url.vendor.onboarding.settings.put(),
      { BusinessName: businessName },
      () => {
        _updateBusinessName(businessName);
        refreshCollateralPreview();
        refreshWebsitePreview();
      },
      handleError,
      [Request.header.apiKey()]
    )
  }

  // Logo
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [logo, setLogo] = useState(undefined);
  const handleLogoFileRead = (event) => {
    if (!event.target || !event.target.files || event.target.files.length == 0) {
      return;
    }
    const file = event.target.files[0];
    if (file.size > 20971520) {
      setError('Maximum upload size is 20MB')
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => handleLogoUpload({ Content: reader.result, Filename: file.name });
    reader.readAsDataURL(file);
  }
  const handleLogoUpload = (data) => {
    setLoadingLogo(true);
    Artcloud.api.put(
      Artcloud.url.vendor.collateral.styleguides.images.logo.put(),
      data,
      (responseData) => {
        setLogo(responseData);
        setLoadingLogo(false);
        refreshCollateralPreview();
      },
      handleError
    );
    Artcloud.api.put(
      Artcloud.url.vendor.website.images.logo.put(),
      data,
      refreshWebsitePreview
    )
    Artcloud.api.put(
      Artcloud.url.vendor.campaigns.settings.logos.put(),
      data,
      (responseData) => {
        if (data && data.Campaign && data.Campaign.Settings) {
          data.Campaign.Settings.Logo = responseData;
        }
        refreshCampaignPreview();
      }
    )
  }

  // Inventory
  const [uploadArtworkVisible, setUploadArtworkVisible] = useState(false);
  const [inventory, setInventory] = useState([]);
  const inventoryRef = React.useRef();
  inventoryRef.current = inventory;
  const [listId, setListId] = useState(undefined);
  useEffect(() => updateWebsitePreviewUrl(websiteSettings.Subdomain), [inventory.length])
  const handleInventoryUpload = (data) => {
    const inventoryListBlock = findCampaignBlock(_campaignConfig, CampaignName.Block.Inventory.List);
    if (inventoryListBlock && inventoryListBlock.config && inventoryListBlock.config.inventory) {
      inventoryListBlock.config.inventory = [];
    }
    setInventory(data.inventory)
    setListId(data.listId);
    getInventoryData({})
  }

  // Actions
  const Styleguide = collateralTemplate.Styleguide || {};
  const handleContinue = () => {
    if (!selectedPackage.BrandPackageId) {
      return;
    }
    if (!Device.isMobile()) {
      switch (activeView) {
        case Preview.Type.Website:
          setActiveView(Preview.Type.Collateral);
          return;
        case Preview.Type.Collateral:
          setActiveView(Preview.Type.Email);
          return;
      }
    }
    setSaving(true);

    Artcloud.api.post(
      Artcloud.url.vendor.brandPackages.claims.post(selectedPackage.BrandPackageId),
      { WithCollateral: true, WithWebsite: false },
      () => {
        let brandPackage;
        for (let i = 0; i < packages.length; i++) {
          if (packages[i].BrandPackageId != selectedPackage.BrandPackageId) {
            continue;
          }
          brandPackage = packages[i];
          break;
        }
        Artcloud.fullstory.event('OB: Brand Selected', { brandPackage: brandPackage ? brandPackage.Name : selectedPackage.BrandPackageId });

        Artcloud.api.post(
          Artcloud.url.vendor.onboarding.campaigns.post(),
          { Name: 'Default Template', Type: 'template' },
          (campaignResponseData) => {
            Artcloud.api.put(
              Artcloud.url.vendor.onboarding.campaigns.configs.put(campaignResponseData.CampaignId),
              campaignConfig,
              () => {
                Artcloud.api.post(
                  Artcloud.url.vendor.collaterals.fonts.post(),
                  { Fonts: Styleguide.Fonts && Styleguide.Fonts.Families },
                  () => {
                    Artcloud.api.put(
                      Artcloud.url.vendor.collaterals.kit.put(),
                      Styleguide.Documents && Styleguide.Documents.Kit,
                      props.onContinue,
                      handleError
                    )
                  },
                  handleError
                )
              },
              handleError,
              [Request.header.apiKey()]
            )
          },
          handleError,
          [Request.header.apiKey()]
        )
      },
      handleError
    )
  }
  // const handlePrint = () => {
  //   if (!data || !collateralTemplate) {
  //     return;
  //   }
  //   let inventory, route, type;
  //   switch (collateralType) {
  //     case Preview.CollateralType.Certificate:
  //       if (data.Certificate) {
  //         inventory = data.Certificate.Inventory;
  //       }
  //       route = Artcloud.router.collaterals.certificates.post();
  //       type = 'Certificate of Authenticity';
  //       break;
  //     case Preview.CollateralType.Pricelist:
  //       if (data.Pricelist) {
  //         inventory = data.Pricelist.Inventory;
  //       }
  //       route = Artcloud.router.collaterals.pricelists.post();
  //       type = 'Pricelist';
  //       break;
  //     case Preview.CollateralType.Tearsheet:
  //       if (data.Tearsheet) {
  //         inventory = data.Tearsheet.Inventory;
  //       }
  //       route = Artcloud.router.collaterals.tearsheets.post();
  //       type = 'Tearsheet';
  //       break;
  //   }
  //   if (!inventory || inventory.length == 0 || !route) {
  //     return;
  //   }
  //   // if (props.onToast) {
  //   //   props.onToast(`Printing Sample ${type}`);
  //   // }
  //   const postData = {
  //     CollateralType: 1,
  //     Config: collateralTemplate.Config,
  //     ConfigType: collateralType,
  //     Disposition: 'Inline',
  //     EditionIds: []
  //   }
  //   inventory.forEach(d => postData.EditionIds.push(d.EditionId));
  //   const params = RenderedCollateral.Types.Document.getPDFParameters(collateralTemplate.Config);
  //   if (params) {
  //     postData.PDF = params;
  //   }
  //   // TODO
  //   Artcloud.data.post(
  //     route,
  //     postData,
  //     (responseData) => {
  //       if (responseData.Document && responseData.Document.DocumentId) {
  //         Artcloud.data.downloadDocument(responseData.Document, true);
  //       } else {
  //         Repository.Export.Pending.add(responseData.ExportId, Repository.Export.Type.Print);
  //         Repository.Export.insert(responseData);
  //         Repository.Export.fetch(props.onError, { owner: 0, status: 0 });
  //       }
  //     },
  //     props.onError
  //   );
  // }
  if (logo) {
    Styleguide.LogoHeight = logo.Height;
    Styleguide.LogoUrl = logo.Url;
    Styleguide.LogoWidth = logo.Width;
  } else if (data && data.Styleguide && data.Styleguide.LogoUrl) {
    Styleguide.LogoHeight = data.Styleguide.LogoHeight;
    Styleguide.LogoUrl = data.Styleguide.LogoUrl;
    Styleguide.LogoWidth = data.Styleguide.LogoWidth;
  }
  const handleLogoUploadClick = () => document.getElementById('logoUpload').click()
  let logoButton;
  if (Styleguide.LogoUrl) {
    logoButton = <button className="logo-cont" style={{ backgroundImage: `url('${Styleguide.LogoUrl}')` }} onClick={handleLogoUploadClick} />
  } else {
    logoButton = <button onClick={handleLogoUploadClick}>Upload Logo</button>
  }

  let collateralData = dataRef.current ? { ...dataRef.current, Styleguide } : { Styleguide };
  switch (collateralType) {
    case Preview.CollateralType.Certificate:
      if (collateralData.Certificate) {
        collateralData.Constants = collateralData.Certificate.Constants;
        collateralData.Gallery = collateralData.Certificate.Gallery;
        collateralData.Inventory = collateralData.Certificate.Inventory;
      }
      break;
    case Preview.CollateralType.Pricelist:
      if (collateralData.Pricelist) {
        collateralData.Constants = collateralData.Pricelist.Constants;
        collateralData.Gallery = collateralData.Pricelist.Gallery;
        collateralData.Inventory = collateralData.Pricelist.Inventory;
      }
      break;
    case Preview.CollateralType.Tearsheet:
      if (collateralData.Tearsheet) {
        collateralData.Constants = collateralData.Tearsheet.Constants;
        collateralData.Gallery = collateralData.Tearsheet.Gallery;
        collateralData.Inventory = collateralData.Tearsheet.Inventory;
      }
      break;
  }
  const collateralTypeOptions = [Preview.CollateralType.Tearsheet, Preview.CollateralType.Pricelist, Preview.CollateralType.Certificate];
  const typeDisplay = props.user ? User.getTypeDisplay(props.user) : '';

  // Fonts
  const [bodyFont, setBodyFont] = useState(undefined);
  const [headerFont, setHeaderFont] = useState(undefined);
  const _getFontLocation = (design) => {
    const search = "font-family:'";
    let start = design.indexOf(search);
    if (start < 0) {
      return;
    }
    start += search.length
    const end = design.indexOf("';", start);
    return { start, end };
  }
  const extractFont = (design) => {
    const fontLocation = _getFontLocation(design);
    if (!fontLocation) {
      return;
    }
    return design.substring(fontLocation.start, fontLocation.end);
  }
  const replaceFont = (design, fontFamily) => {
    const fontLocation = _getFontLocation(design);
    if (!fontLocation) {
      return;
    }
    return design.substring(0, fontLocation.start) + fontFamily + design.substring(fontLocation.end);
  }
  useEffect(() => {
    if (selectedPackage.BrandPackageId &&
      packageConfigs[selectedPackage.BrandPackageId] &&
      packageConfigs[selectedPackage.BrandPackageId].Website &&
      packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide &&
      packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Fonts) {
      setBodyFont(packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Fonts.Body);
      setHeaderFont(packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Fonts.Header);
    }
  }, [selectedPackage.BrandPackageId]);
  const handleFontSelect = (name, data) => {
    let newFonts;
    switch (name) {
      case 'bodyFont':
        newFonts = [data.font]
        if (cannedFonts) {
          for (let i = 0; i < cannedFonts.length; i++) {
            if (cannedFonts[i].Family != headerFont) {
              continue;
            }
            newFonts.push(cannedFonts[i]);
            break;
          }
        }
        setBodyFont(data.font.Family);
        break;
      case 'headerFont':
        newFonts = [data.font]
        if (cannedFonts) {
          for (let i = 0; i < cannedFonts.length; i++) {
            if (cannedFonts[i].Family != bodyFont) {
              continue;
            }
            newFonts.push(cannedFonts[i]);
            break;
          }
        }
        setHeaderFont(data.font.Family);
        break;
    }
    if (!selectedPackage.BrandPackageId || !packageConfigs[selectedPackage.BrandPackageId]) {
      return;
    }
    // Website Styleguide
    if (packageConfigs[selectedPackage.BrandPackageId].Website &&
      packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide &&
      packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Fonts) {
      // Website Fonts
      packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Fonts.Families = newFonts;

      // Website Design Kit
      let global;
      if (packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents &&
        packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit &&
        packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global) {
        global = {};
        for (let key in packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global) {
          if (!packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global[key].design) {
            continue;
          }
          let fontName = 'headerFont';
          switch (key) {
            case 'p':
              fontName = 'bodyFont';
              break;
          }
          if (name != fontName) {
            global[key] = packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global[key];
            continue;
          }
          global[key] = { design: replaceFont(packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global[key].design, data.font.Family) }
        }
        packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit.global = global;
      }
      Artcloud.api.post(
        Artcloud.url.vendor.websites.fonts.post(),
        { Fonts: newFonts },
        () => {
          if (global) {
            const kit = { ...packageConfigs[selectedPackage.BrandPackageId].Website.Styleguide.Documents.Kit };
            kit.global = global;
            Artcloud.api.put(
              Artcloud.url.vendor.websites.configs.kit.put(),
              kit,
              refreshWebsitePreview,
              handleError
            )
          } else {
            refreshWebsitePreview();
          }
        },
        handleError
      )
    }
    // Collateral Styleguide
    if (packageConfigs[selectedPackage.BrandPackageId].Collateral &&
      packageConfigs[selectedPackage.BrandPackageId].Collateral.Configs &&
      packageConfigs[selectedPackage.BrandPackageId].Collateral.Configs.length > 0) {
      let fonts, global;
      packageConfigs[selectedPackage.BrandPackageId].Collateral.Configs.forEach(d => {
        if (!d.Styleguide || !d.Styleguide.Fonts) {
          return;
        }
        // Collateral Fonts
        if (!fonts) {
          fonts = { Families: [] };
          const linkHrefs = [];
          newFonts.forEach(font => {
            fonts.Families.push(font);

            let temp = font.Family;
            if (font.Variants && font.Variants.length > 0) {
              temp += `:${font.Variants.join(',')}`
            }
            linkHrefs.push(temp);
          })
          fonts.Families.push(data.font);
          linkHrefs.push(`${data.font.Family}${data.font.Variants && data.font.Variants.length > 0 ? `:${data.font.Variants.join(',')}` : ''}`);
          fonts.LinkHref = `https://fonts.googleapis.com/css?family=${linkHrefs.join('|')}&display=swap`
        }
        d.Styleguide.Fonts = fonts;
        if (!d.Styleguide.Documents || !d.Styleguide.Documents.Kit) {
          return;
        }
        if (global) {
          d.Styleguide.Documents.Kit.global = global;
          return;
        }
        // Collateral Design Kit
        global = {};
        for (let key in d.Styleguide.Documents.Kit.global) {
          if (!d.Styleguide.Documents.Kit.global[key].design) {
            continue;
          }
          let fontName = 'bodyFont';
          switch (key) {
            case 'h1':
            case 'h4':
              fontName = 'headerFont';
              break;
          }
          if (name != fontName) {
            global[key] = d.Styleguide.Documents.Kit.global[key];
            continue;
          }
          global[key] = { design: replaceFont(d.Styleguide.Documents.Kit.global[key].design, data.font.Family) }
        }
        d.Styleguide.Documents.Kit.global = global;
      })
      refreshCollateralPreview();
    }
  }

  const handleBrandPackageSelectionClose = () => {
    if (Device.isMobile()) {
      handleContinue();
    } else {
      setBrandPackageSelectionVisible(false);
    }
  }
  const brandPackageSelection = (
    <BrandPackageSelection
      packages={packages}
      selectedPackage={selectedPackage}
      onClose={handleBrandPackageSelectionClose}
      onSelect={handleSelect} />
  );
  if (Device.isMobile()) {
    return (
      <>
        {brandPackageSelection}
        {
          saving &&
          <ModalBackground style={{ position: 'fixed', zIndex: 1 }} />
        }
      </>
    );
  }
  const _campaignConfig = campaignConfig;
  let campaignData;
  switch (activeView) {
    case Preview.Type.Email:
      campaignData = data && data.Campaign && data.Campaign.Settings || {};
      if (logo) {
        campaignData.Logo = logo;
      }
      const inventoryListBlock = findCampaignBlock(_campaignConfig, CampaignName.Block.Inventory.List);
      if (inventoryListBlock && inventoryListBlock.config && inventoryListBlock.config.inventory && inventoryListBlock.config.inventory.length == 0) {
        let _inventory;
        if (inventory.length > 0) {
          _inventory = inventory;
        } else if (data && data.Campaign && data.Campaign.Inventory && data.Campaign.Inventory.Data) {
          _inventory = data.Campaign.Inventory.Data;
        }
        if (_inventory) {
          inventoryListBlock.config.inventory = _inventory.map(d => ({
            data: { ...d },
            type: 'inventory',
            useDefaultCellDesign: true
          }))
        }
      }
      break;
  }
  return (
    <>
      {
        !uploadArtworkVisible &&
        <div className="gallery-preview">
          {controlsClosed && <img src="/assets/img/onboarding/rounded-back.svg" className="open-controls" onClick={() => { setControlsClosed(false); }} />}
          <div className={`controls ${controlsClosed ? "closed" : "open"}`}>
            <img src="/assets/img/onboarding/rounded-back.svg" className="close-controls" onClick={() => { setControlsClosed(true); }} />
            <img className="logo" src="/assets/img/logos/header-mark-left.svg" />
            <h1>Preview ArtCloud</h1>
            <p>Give ArtCloud a spin. Have fun and if you like what you create here, it will be saved once you set up an account.</p>
            {
              props.user.Type != undefined &&
              <>
                <div className="input-wrapper">
                  <label>{typeDisplay} Name</label>
                  <Input
                    name="BusinessName"
                    type="text"
                    value={businessName != undefined ? businessName : props.user.BusinessName}
                    onBlur={handleBusinessNameBlur}
                    onChange={handleBusinessNameChange} />
                </div>
                <div className="input-wrapper with-button">
                  <label>Current Theme</label>
                  <input disabled={true} type="text" defaultValue={loadingBrandPackageConfig ? '' : selectedPackage && selectedPackage.Name} />
                  <button onClick={() => setBrandPackageSelectionVisible(true)}>Change theme</button>
                </div>
                <div className="input-wrapper">
                  <label>Header Font</label>
                  {/* <FontDropDownSearchable
                    name="headerFont"
                    value={headerFont}
                    onError={handleError}
                    onSelect={handleFontSelect} /> */}
                  <FontDropDownCanned
                    fonts={cannedFonts}
                    name="headerFont"
                    value={headerFont}
                    onSelect={handleFontSelect} />
                </div>
                <div className="input-wrapper">
                  <label>Body Font</label>
                  {/* <FontDropDownSearchable
                    name="bodyFont"
                    value={bodyFont}
                    onError={handleError}
                    onSelect={handleFontSelect} /> */}
                  <FontDropDownCanned
                    fonts={cannedFonts}
                    name="bodyFont"
                    value={bodyFont}
                    onSelect={handleFontSelect} />
                </div>
                <div className="button-wrapper with-icon">
                  <label>Logo</label>
                  {logoButton}
                  <input id="logoUpload" accept="image/png, image/jpg, image/jpeg, image/bmp, image/svg+xml, image/gif" style={{ display: 'none' }} type="file" onChange={handleLogoFileRead} />
                  {Styleguide.LogoUrl == undefined && <img src="/assets/img/onboarding/upload-artwork.svg" />}
                </div>
                {
                  (!inventory || inventory.length == 0) ?
                    <div className="select-wrapper with-contextual-link">
                      <label>Stock Inventory</label>
                      <select onChange={handleSampleSetSelect}>
                        {
                          isArtist() ?
                            <option label="Samples" value="samples" />
                            :
                            <>
                              {sampleSetOptions.map(d => <option key={d.value} {...d} />)}
                            </>
                        }
                      </select>
                      <p onClick={() => setUploadArtworkVisible(true)}> or upload my own inventory
                      </p>
                    </div>
                    :
                    <div className="button-wrapper">
                      <label>Inventory</label>
                      <button onClick={() => setUploadArtworkVisible(true)}>Customize Inventory</button>
                    </div>
                }
              </>
            }
          </div>
          <div className={`view ${controlsClosed ? "" : "controls-open"}`}>
            <div className="toolbar">
              <div onClick={() => { setActiveView(Preview.Type.Website); }} className={`control-wrapper ${activeView == Preview.Type.Website ? "active" : ""}`} >
                <Website fill={activeView == Preview.Type.Website ? "#00AEFF" : "#ACACAC"} />
                <span>Website</span>
                {activeView == Preview.Type.Website && <div className="indicator" />}
              </div>
              <div onClick={() => { setActiveView(Preview.Type.Collateral); }} className={`control-wrapper ${activeView == Preview.Type.Collateral ? "active" : ""}`} >
                <Collateral fill={activeView == Preview.Type.Collateral ? "#00AEFF" : "#ACACAC"} />
                <span>Collateral</span>
                {activeView == Preview.Type.Collateral && <div className="indicator" />}
              </div>
              <div onClick={() => { setActiveView(Preview.Type.Email); }} className={`control-wrapper ${activeView == Preview.Type.Email ? "active" : ""}`} >
                <Email fill={activeView == Preview.Type.Email ? "#00AEFF" : "#ACACAC"} />
                <span>Email</span>
                {activeView == Preview.Type.Email && <div className="indicator" />}
              </div>
              <div className="edit" onClick={handleContinue} >
                <span>Continue</span>
              </div>
            </div>
            <div className="toolbar--mobile">
              <div>
                <ArrowLeft fill={activeView === Preview.Type.Email || activeView === Preview.Type.Collateral ? "#00AEFF" : "#ACACAC"} onClick={() => { if (activeView === Preview.Type.Email) { setActiveView(Preview.Type.Collateral); } if (activeView === Preview.Type.Collateral) { setActiveView(Preview.Type.Website); } }} />
                <div className="active-view">{activeView}</div>
                <ArrowRight fill={activeView === Preview.Type.Website || activeView === Preview.Type.Collateral ? "#00AEFF" : "#ACACAC"} onClick={() => { if (activeView === Preview.Type.Website) { setActiveView(Preview.Type.Collateral); } if (activeView === Preview.Type.Collateral) { setActiveView(Preview.Type.Email); } }} />
              </div>
            </div>
            <div className="inner-view">
              <div id="workspace">
                {
                  activeView == Preview.Type.Website &&
                  !loadingWebsitePreview &&
                  websitePreviewUrl &&
                  <iframe
                    id="websitePreviewFrame"
                    className="preview"
                    src={websitePreviewUrl}
                    style={{ opacity: showLoading ? 0 : 1 }}
                    onLoad={() => setShowLoading(false)} />
                }
                {
                  activeView == Preview.Type.Collateral &&
                  collateralData &&
                  !loadingBrandPackageConfig &&
                  !loadingLogo &&
                  collateralTemplate.CollateralTemplateId &&
                  <>
                    <div style={{ color: '#ffffff', display: 'flex', fontSize: '18px', justifyContent: 'center', margin: 'auto', maxWidth: '8.5in', paddingBottom: '20px' }}>
                      <div>
                        {collateralTypeOptions.map((d, i) =>
                          <React.Fragment key={d}>
                            <button
                              style={{ color: collateralType == d ? '#00b1ff' : 'inherit', cursor: 'pointer', fontSize: 'inherit' }}
                              onClick={() => handleCollateralTemplateTypeChange(d)}>
                              {Preview.CollateralType.display(d)}
                            </button>
                            {i < collateralTypeOptions.length - 1 && <span style={{ cursor: 'default', margin: '0 15px' }}>|</span>}
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                    <div id="collateralPreview" className="preview">
                      <CollateralFrame
                        collateralType={2}
                        config={{ ...collateralTemplate.Config, id: `${selectedPackage.BrandPackageId}-${collateralTemplate.CollateralTemplateId}` }}
                        configType={collateralType}
                        data={collateralData}
                        forcePageHeader={true}
                        preview={true}
                        style={{ height: '100%', opacity: loadingCollateralPreview ? 0 : 1, width: '100%', }}
                        styles={collateralPreviewStyles}
                        onLoad={handleCollateralPreviewLoad} />
                    </div>
                  </>
                }
                {
                  activeView == Preview.Type.Email &&
                  !loadingBrandPackageConfig &&
                  _campaignConfig &&
                  <CampaignFrame
                    builder={false}
                    contentConfig={_campaignConfig}
                    data={campaignData}
                    pageType="campaignTemplate"
                    renderMso={true}
                    // session={this.props.session}
                    style={{ display: 'block', height: '100%', margin: 'auto', width: '805px' }}
                    styles="#frameContent{background-color:#ffffff;}" />
                }
              </div>
            </div>
          </div>
          {brandPackageSelectionVisible && brandPackageSelection}
          {
            error &&
            error.length > 0 &&
            <ModalBackground style={{ opacity: '0.9' }} onClick={clearError}>
              <div className="input-wrapper with-button" style={{ fontSize: '16px' }}>
                {error}
                <button onClick={clearError}>Close</button>
              </div>
            </ModalBackground>
          }
          {
            saving &&
            <ModalBackground style={{ zIndex: 1 }} />
          }
        </div>
      }
      {
        uploadArtworkVisible &&
        <UploadArtwork
          inventory={inventory}
          listId={listId}
          user={props.user}
          onClose={() => setUploadArtworkVisible(false)}
          onContinue={handleInventoryUpload}
          onError={props.onError} />
      }
    </>
  )
}

function BrandPackageSelection(props) {
  const [selectedPackage, setSelectedPackage] = useState(props.selectedPackage);
  const selectPackage = (d, closeOnSelect) => {
    setSelectedPackage(d);
    if (closeOnSelect) {
      handleContinue(d);
    }
  }
  const handleContinue = (d) => {
    props.onSelect(d || selectedPackage);
    props.onClose();
  }
  return (
    <ModalBackground className="brand-selection" style={{ justifyContent: 'flex-start', opacity: 1 }}>
      <div className="grid--onboarding">
        <div className="form-header">
          <CloseButton onClick={props.onClose} />
          <h1>Pick a Theme</h1>
          <p>ArtCloud themes include a website and collateral.</p>
        </div>
        <div className="form-body">
          <div className="form-body__grid--boxes form-body__grid--boxes--2">
            {
              props.packages &&
              props.packages.map(d =>
                <BrandPackageThemeContainer
                  key={d.BrandPackageId}
                  active={selectedPackage && d.BrandPackageId == selectedPackage.BrandPackageId}
                  d={d}
                  onSelect={selectPackage} />
              )
            }
          </div>
        </div>
        <div className="form-body__footer">
          <MantineButton size="lg" onClick={() => handleContinue()}>
            Continue
          </MantineButton>
        </div>
      </div>
    </ModalBackground>
  )
}

function BrandPackageThemeContainer(props) {
  const d = props.d || {};
  const handleSelectPackage = (closeOnSelect) => props.onSelect(d, closeOnSelect != false);
  return (
    <div className={`theme__cont${props.active ? ' theme__cont--active' : ''}`} onClick={() => handleSelectPackage(false)}>
      <img alt={d.Name} src={d.Image && d.Image.MediumUrl} />
      <div className="name-description">
        <h2 className="name">{d.Name}</h2>
        <p className="description">{d.Tags && d.Tags.join(', ')}</p>
      </div>
      <div className="preview-theme-buttons-container" onClick={handleSelectPackage}>
        <div className="preview-theme-buttons">
          <button className="continue-with-theme">
            Continue with {`${d.Name}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// #region COMPONENTS

function Brand(props) {
  return (
    <div className={`logo${props.isArtsy ? ' logo--artsy' : ''}`}>
      <img src="/assets/img/logos/header-mark-left.svg" />
      {
        props.isArtsy &&
        <>
          <span className="x">X</span>
          <img src="/assets/img/logos/artsy.png" />
        </>
      }
    </div>
  )
}

function CloseButton(props) { return <img className="close" src="/assets/img/onboarding/close.svg" {...props} /> }

class DropDown extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value || ''
    }

    this.handleClick = this.handleClick.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value != this.props.value && this.props.value != this.state.value) {
      this.setState({
        value: this.props.value
      })
    }
  }

  handleClick() {
    this.setState({
      contentVisible: !this.state.contentVisible
    })
  }

  handleSelect(item) {
    if (this.props.onSelect) {
      this.props.onSelect(this.props.name, item);
    }
    this.setState({
      contentVisible: false
    });
  }

  render() {
    return (
      <div className="dropdown-wrapper">
        <div className="dropdown-button-wrapper" onClick={this.handleClick}>
          <button className={`${this.state.contentVisible ? "dropdown-open" : ""}`}>
            {this.state.value}
          </button>
          <img className="dropdown-chevron" src="/assets/img/onboarding/chevron-down.svg" />
        </div>
        <div className={`dropdown-results ${this.state.contentVisible && this.props.options?.length > 0 ? "show" : ""}`}>
          {this.props.options.map(item => <DropDownItem key={item.value} item={item} name={item.value} onSelect={this.handleSelect}>{item.display}</DropDownItem>)}
        </div>
        {
          this.state.contentVisible &&
          this.props.options?.length > 0 &&
          <div style={{ backgroundColor: 'transparent', height: '100%', left: 0, position: 'fixed', top: 0, width: '100%', zIndex: 1 }} onClick={this.handleClick}></div>
        }
      </div>
    )
  }
}

function DropDownItem(props) {
  const handleMouseDown = () => {
    if (props.onMouseDown) {
      props.onMouseDown(props.item);
    }
  }
  return (
    <span onClick={() => props.onSelect(props.item)} onMouseDown={handleMouseDown}>
      <input
        id={props.name}
        disabled={true}
        name={props.name}
        style={{ display: "none" }}
        type="radio"
        value={props.name} />
      <label htmlFor={props.name}>{props.children}</label>
    </span>
  )
}

function FontDropDownCanned(props) {
  const fonts = props.fonts || [];
  const options = fonts.map(d => ({
    display: <span style={{ fontFamily: `'${d.Family}'`, padding: 0 }}>{d.Family}<link href={`https://fonts.googleapis.com/css?family=${d.Family}`} rel="stylesheet" /></span>,
    font: d,
    value: d.Family
  }));
  return <DropDown {...props} options={options} />
}

function LabelWithUseAll(props) {
  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: '12px' }}>
      <label>{props.children}</label><button style={{ color: '#777777', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', marginTop: '2px', textTransform: 'uppercase' }} onClick={() => props.onUseAll(props.name)}>Use For All Items</button>
    </div>
  )
}

function ModalBackground(props) {
  let style = { backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', left: 0, opacity: '0.7', position: 'absolute', textAlign: 'center', top: 0, width: '100%' };
  if (props.style) {
    style = { ...style, ...props.style }
  }
  return <div className={props.className} style={style} onClick={props.onClick}>{props.children}</div>
}

class SearchableDropDown extends React.Component {
  constructor(props) {
    super(props);

    const initialValue = props.value || '';
    this.state = {
      initialValue,
      value: initialValue
    };

    this._handleInputRef = this._handleInputRef.bind(this);
    this._searchTermItem = this._searchTermItem.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleResultsRef = this.handleResultsRef.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.reset = this.reset.bind(this);
  }

  componentDidMount() {
    this._handleSearch = _.debounce(() => {
      if (!this.state.value || this.state.value.length == 0) {
        return;
      }
      const query = this.props.query ? { ...this.props.query } : {};
      query[this.props.searchKey || 'q'] = this.state.value;
      this.handleSearch(query);
    }, 500)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value != this.props.value && this.props.value != this.state.value) {
      this.setState({
        initialValue: this.props.value,
        value: this.props.value
      })
    }
  }

  _handleInputRef(ref) {
    this.inputRef = ref;
  }

  _searchTermItem() {
    return { display: this.state.value, isNew: true, value: this.state.value };
  }

  handleBlur() {
    if (this.state.blurBlocked) {
      this.setState({
        blurBlocked: false
      })
      return;
    }
    this.setState({
      data: undefined,
      value: this.state.initialValue
    })
    setTimeout(this.reset, 100);
  }

  handleChange(e) {
    if (!e.target) {
      return;
    }
    const value = e.target.value || '';
    this.setState({
      data: value.length > 0 ? [] : undefined,
      searching: value.length > 0,
      value
    }, this._handleSearch)
  }

  handleKeyUp(event) {
    if (event.keyCode !== 13 || !this.state.data || this.state.searching) {
      // Only interested in Enter key press
      return;
    }
    if (this.state.data.length == 0) {
      if (this.props.showSearchTerm == false) {
        // No results and search term selection disabled
        return;
      }
      // Select search term
      this.handleSelect(this._searchTermItem());
    } else {
      if (this.state.data[0].display == this.state.value) {
        this.handleSelect(this.state.data[0]);
      } else {
        // Select search search term
        this.handleSelect(this._searchTermItem());
      }
    }
    if (this.inputRef) {
      this.setState({
        blurBlocked: true
      }, () => this.inputRef.blur());
    }
  }

  handleLoadMore() {
    if (this.state.searching) {
      return;
    }
    this.setState({
      searching: true
    })
    const query = this.props.query ? { ...this.props.query } : {};
    query[this.props.searchKey || 'q'] = this.state.value;
    if (this.state.data) {
      query.offset = this.state.data.length;
    }
    this.handleSearch(query);
  }

  handleResultsRef(ref) {
    this.resultsRef = ref;
  }

  handleSearch(query) {
    query.limit = 40;

    const handleData = (responseData) => {
      const data = this.state.data || []
      this.setState({
        data: data.concat(this.props.onParseData(this.props.name, responseData)),
        hasMore: responseData.HasMore,
        searching: false
      });
    }
    if (this.props.onSearch) {
      this.props.onSearch(query, handleData)
    } else {
      Artcloud.api.get(
        this.props.route,
        query,
        handleData,
        this.props.onError
      )
    }
  }

  handleSelect(d) {
    this.setState({
      blurBlocked: true,
      initialValue: d.display,
      value: d.display
    }, this.reset);
    if (this.props.onSelect) {
      this.props.onSelect(this.props.name, d);
    }
  }

  reset() {
    this.setState({
      data: undefined,
      searching: false
    })
  }

  render() {
    let foundSearchTerm = false;
    const content = [];
    if (this.state.data && this.state.data.length > 0) {
      this.state.data.forEach((d, i) => {
        if (d.display == this.state.value) {
          foundSearchTerm = true;
        }
        content.push(<DropDownItem key={d.display} item={d} name={d.display} onMouseDown={this.handleSelect}>{d.content || d.display}</DropDownItem>)
        if (this.state.data.length > 10 && this.state.hasMore && i == this.state.data.length - 10) {
          content.push(<Landmark key="loadMore" containerRef={this.resultsRef} onEnter={this.handleLoadMore} />)
        }
      })
    }
    if (!this.state.searching) {
      if (this.props.showSearchTerm == false && content.length == 0) {
        content.splice(0, 0, <span key="empty" style={{ cursor: 'default' }}>No results</span>)
      } else if (this.props.showSearchTerm != false && !foundSearchTerm) {
        content.splice(0, 0, <DropDownItem key="new" item={this._searchTermItem()} name="new" onMouseDown={this.handleSelect}>{this.state.value}</DropDownItem>);
      }
    }
    const renderResults = this.state.data != undefined || this.state.searching;
    return (
      <>
        <div className="input-wrapper search">
          {this.props.label}
          <div className="search-field">
            <input
              ref={this._handleInputRef}
              className={`${this.state.data != undefined || this.state.searching ? "items-found" : ""}`}
              name={this.props.name}
              type="text"
              value={this.state.value}
              onBlur={this.handleBlur}
              onChange={this.handleChange}
              onKeyUp={this.handleKeyUp} />
            <img
              className="search-icon"
              height="15px"
              src="/assets/img/icons/search.png"
              width="15px" />
          </div>
        </div>
        <div className={`search-results${renderResults ? '' : ' dn'}`} ref={this.handleResultsRef}>
          {
            renderResults &&
            <>
              {content}
              {(this.state.searching || this.props.loading) && <span>Searching...</span>}
            </>
          }
        </div>
      </>
    )
  }
}

// #endregion COMPONENTS

// #region SVG

function Collateral(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={21}
      height={25}
      fill="#ACACAC"
      {...props}
    >
      <path
        fillRule="evenodd"
        d="M3.442 2.273c-.634 0-1.147.508-1.147 1.136v18.182c0 .628.513 1.136 1.147 1.136H17.21c.634 0 1.147-.508 1.147-1.136V9.366c0-.093-.006-.184-.018-.275h-5.718a1.141 1.141 0 0 1-1.148-1.137V2.29a2.306 2.306 0 0 0-.278-.018H3.442ZM13.768 3.88v2.938h2.967L13.768 3.88ZM0 3.41C0 1.526 1.542 0 3.442 0h7.754c1.216 0 2.384.48 3.244 1.332l4.868 4.82a4.524 4.524 0 0 1 1.344 3.214v12.225c0 1.882-1.542 3.409-3.442 3.409H3.442C1.542 25 0 23.473 0 21.59V3.41Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function Email(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={30}
      height={26}
      fill="#ACACAC"
      {...props}
    >
      <path d="M27.857 18.417V7.583c0-1.794-1.44-3.25-3.214-3.25H23.57V3.25c0-1.794-1.44-3.25-3.214-3.25H9.643C7.868 0 6.429 1.456 6.429 3.25v1.083H5.357c-1.774 0-3.214 1.456-3.214 3.25v10.834c-1.184 0-2.143.97-2.143 2.166v1.084c0 1.15.451 2.252 1.255 3.064A4.262 4.262 0 0 0 4.285 26h21.43a4.262 4.262 0 0 0 3.03-1.269A4.358 4.358 0 0 0 30 21.667v-1.084c0-.574-.226-1.125-.628-1.531a2.131 2.131 0 0 0-1.515-.635ZM9.643 2.167h10.714c.184.004.362.058.518.154l-5.28 3.56c-.36.244-.83.244-1.19 0l-5.28-3.56c.156-.096.334-.15.518-.154ZM8.57 4.55l4.646 3.132a3.18 3.18 0 0 0 3.565 0L21.43 4.55v6.283c0 .287-.113.564-.314.767-.2.203-.474.317-.758.317H9.643a1.077 1.077 0 0 1-1.072-1.084V4.55ZM4.286 7.583c0-.598.48-1.083 1.071-1.083H6.43v4.333c0 1.795 1.44 3.25 3.214 3.25h10.714c1.775 0 3.214-1.455 3.214-3.25V6.5h1.072c.284 0 .557.114.758.317.2.203.313.48.313.766v10.834H4.286V7.583Zm23.571 14.084c0 .574-.226 1.125-.628 1.531a2.13 2.13 0 0 1-1.515.635H4.286a2.154 2.154 0 0 1-2.143-2.166v-1.084h25.714v1.084Z" />
    </svg>
  );
}

function Website(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={27}
      height={26}
      fill="#ACACAC"
      {...props}
    >
      <path d="M22.588 0H4.244A4.243 4.243 0 0 0 0 4.233v17.19a4.243 4.243 0 0 0 4.244 4.232H7.57a1.179 1.179 0 1 0 0-2.358H4.244c-1.04 0-1.886-.84-1.886-1.875V9.35h22.117v12.073a1.882 1.882 0 0 1-1.887 1.875H6.285a1.179 1.179 0 1 0 0 2.358h16.303a4.243 4.243 0 0 0 4.245-4.233V4.232A4.243 4.243 0 0 0 22.588 0ZM2.358 6.991V4.232c0-1.034.846-1.874 1.886-1.874h18.344c1.04 0 1.887.84 1.887 1.874v2.76H2.358Z" />
      <path d="M5.948 3.494c-1.55.027-1.549 2.332 0 2.358 1.55-.026 1.549-2.331 0-2.358ZM10.815 3.494c-1.55.027-1.549 2.332 0 2.358 1.55-.026 1.55-2.331 0-2.358ZM15.683 3.494c-1.55.027-1.549 2.332 0 2.358 1.55-.026 1.55-2.331 0-2.358Z" />
    </svg>
  );
}

function ArrowLeft(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlSpace="preserve"
      width={16}
      height={16}
      {...props}
    >
      <path d="M11.6 1.2 5.9 8l5.7 6.8c.3.3.2.8-.1 1-.3.3-.8.2-1-.1L4.4 8.5c-.1-.2-.1-.3-.1-.5s.1-.3.2-.5L10.6.2c.3-.3.7-.3 1-.1.2.3.2.8 0 1.1z" />
    </svg>
  );
}

function ArrowRight(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlSpace="preserve"
      width={16}
      height={16}
      {...props}
    >
      <path d="M4.4 14.8 10.1 8 4.4 1.2c-.3-.3-.2-.8.1-1 .3-.3.8-.2 1 .1l6.1 7.3c.1.1.2.3.2.5s-.1.3-.2.5l-6.1 7.3c-.3.3-.7.3-1 .1-.3-.4-.3-.9-.1-1.2z" />
    </svg>
  );
}

// #endregion SVG

export { BrandPackagePreview, Onboarding };